import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomerPortalBillGroup,
  CustomerPortalInvoice,
  CustomerPortalPaymentMethod,
  CustomerPortalPaymentResponse,
  CustomerPortalSessionData,
  PaymentGatewayConfigurations,
  PaymentMethodStatusEnum,
  PaymentMethodTypeEnum,
  PaymentStatusEnum,
} from '@monetize/types/app';
import { formatCurrency, toDateShort } from '@monetize/utils/core';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  useCreateCustomerPortalPaymentMethod,
  useCustomerPortalPayInvoice,
  useGenerateCustomerPortalPaymentMethodToken,
} from '../../../../api/customerPortalService';
import {
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MPageLoader,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../../../components/Monetize';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { StripeWrapper } from '../../../../components/PaymentMethods/Form/StripeWrapper';
import { PaymentMethodItemRenderer } from '../../../../components/PaymentMethods/PaymentMethodItemRenderer';
import { useToast } from '../../../../services/toast';
import {
  getGroupedAndSortedPaymentMethodOptions,
  getPaymentAmountLimits,
  stripeTypeToPaymentMethodType,
  validatePaymentAmountLimits,
} from '../../../../utils/payment.utils';

const FORM_ID = 'pay-invoice-form';

// The payment endpoint returns HTTP 200 even when the gateway declines the
// charge — the outcome is carried in the response `status`. Treat only a hard
// FAILED/CANCELED as a decline; SUCCESS/SUCCEEDED/PROCESSING/PENDING all mean
// the payment was accepted (some settle asynchronously).
const DECLINED_PAYMENT_STATUSES = new Set<PaymentStatusEnum>([
  PaymentStatusEnum.FAILED,
  PaymentStatusEnum.CANCELED,
]);

const PAYMENT_DECLINED_MESSAGE =
  'Your payment could not be completed. Please try a different payment method or contact support.';

const PAYMENT_SUCCESS_TOAST = {
  severity: 'success',
  summary: 'Success',
  detail: 'Your payment was submitted successfully.',
} as const;

const isPaymentDeclined = (response: CustomerPortalPaymentResponse) =>
  DECLINED_PAYMENT_STATUSES.has(response.status);

interface PayInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: CustomerPortalInvoice;
  sessionData: CustomerPortalSessionData;
  billGroup: CustomerPortalBillGroup;
  paymentMethods: CustomerPortalPaymentMethod[];
  paymentConfiguration?: PaymentGatewayConfigurations;
}

export const PayInvoiceModal = ({ isOpen, ...rest }: PayInvoiceModalProps) => {
  if (!isOpen) {
    return null;
  }
  return <PayInvoiceModalInner {...rest} />;
};

const PayInvoiceModalInner = ({
  onClose,
  invoice,
  sessionData,
  billGroup,
  paymentMethods,
  paymentConfiguration,
}: Omit<PayInvoiceModalProps, 'isOpen'>) => {
  const paymentGatewayId = billGroup.defaultPaymentGatewayId;

  // No gateway configured — show the modal with an inline error. The user
  // can't pay (no PM flow works without a gateway), so we don't render the
  // form at all.
  if (!paymentGatewayId) {
    return (
      <PayInvoiceModalShell onClose={onClose}>
        <AmountDueHeader invoice={invoice} />
        <MBox mt={3} color="tRed.base" fontSize="sm">
          No payment gateway is configured for this account. Please contact
          support.
        </MBox>
      </PayInvoiceModalShell>
    );
  }

  return (
    <PayInvoiceForm
      onClose={onClose}
      invoice={invoice}
      sessionData={sessionData}
      billGroup={billGroup}
      paymentMethods={paymentMethods}
      paymentConfiguration={paymentConfiguration}
      paymentGatewayId={paymentGatewayId}
    />
  );
};

const ExistingPaymentMethodSchema = z.object({
  paymentMethodId: z.string().min(1, 'Payment method is required'),
});
type ExistingPaymentMethodFormValues = z.infer<
  typeof ExistingPaymentMethodSchema
>;

const NewPaymentMethodSchema = z.object({
  paymentMethodName: z
    .string()
    .trim()
    .min(1, 'Payment method name is required'),
  billingDetails: z.object({
    fullName: z.string().trim().min(1, 'Full name is required'),
    email: z
      .string()
      .trim()
      .refine((value) => !value || z.email().safeParse(value).success, {
        message: 'Enter a valid email address',
      }),
    phone: z.string().trim().optional(),
  }),
});
type NewPaymentMethodFormValues = z.infer<typeof NewPaymentMethodSchema>;

interface PayInvoiceFormProps {
  onClose: () => void;
  invoice: CustomerPortalInvoice;
  sessionData: CustomerPortalSessionData;
  billGroup: CustomerPortalBillGroup;
  paymentMethods: CustomerPortalPaymentMethod[];
  paymentConfiguration?: PaymentGatewayConfigurations;
  paymentGatewayId: string;
}

/**
 * Footer state lifted up from whichever tab section is currently mounted.
 * Sections register their submitting/disabled state via `setFooterState` so
 * the modal's footer slot can render a single Cancel / Pay row.
 */
interface SectionFooterState {
  isSubmitting: boolean;
  isPayDisabled: boolean;
}

const PayInvoiceForm = ({
  onClose,
  invoice,
  sessionData,
  billGroup,
  paymentMethods,
  paymentConfiguration,
  paymentGatewayId,
}: PayInvoiceFormProps) => {
  const activePaymentMethods = useMemo(
    () =>
      paymentMethods.filter(
        ({ paymentMethodType, status }) =>
          paymentMethodType !== PaymentMethodTypeEnum.US_BANK_TRANSFER &&
          status === PaymentMethodStatusEnum.ACTIVE,
      ),
    [paymentMethods],
  );

  // US_BANK_TRANSFER can't be charged through the portal — it's a
  // wire-transfer destination. Surface it separately as payment instructions.
  const wireTransferMethod = useMemo(
    () =>
      paymentMethods.find(
        ({ paymentMethodType, status }) =>
          paymentMethodType === PaymentMethodTypeEnum.US_BANK_TRANSFER &&
          status === PaymentMethodStatusEnum.ACTIVE,
      ),
    [paymentMethods],
  );

  const hasExistingPaymentMethods = activePaymentMethods.length > 0;
  const [paymentType, setPaymentType] = useState<'existing' | 'new'>(
    hasExistingPaymentMethods ? 'existing' : 'new',
  );
  const isUsingNewPm = !hasExistingPaymentMethods || paymentType === 'new';

  const { mutateAsync: payInvoice, isPending: isPaying } =
    useCustomerPortalPayInvoice();
  const { mutateAsync: createPaymentMethod, isPending: isCreatingPm } =
    useCreateCustomerPortalPaymentMethod();

  const [footerState, setFooterState] = useState<SectionFooterState>({
    isSubmitting: false,
    isPayDisabled: true,
  });

  return (
    <PayInvoiceModalShell
      onClose={onClose}
      footer={
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton
            type="button"
            onClick={onClose}
            variant="cancel"
            isDisabled={footerState.isSubmitting}
            minW="auto"
          >
            Cancel
          </MButton>
          <MButton
            type="submit"
            form={FORM_ID}
            variant="primary"
            isLoading={footerState.isSubmitting}
            isDisabled={footerState.isPayDisabled}
            minW="auto"
          >
            Pay
          </MButton>
        </MStack>
      }
    >
      <AmountDueHeader invoice={invoice} />

      {hasExistingPaymentMethods && (
        <MRadioGroup
          value={paymentType}
          onChange={(value) => setPaymentType(value as 'existing' | 'new')}
        >
          <MStack mt={4} mb={2} gap={2}>
            <MRadio value="existing">
              <MText fontSize="sm" ml={2} fontWeight="bold">
                Existing Payment Method
              </MText>
            </MRadio>
            {!isUsingNewPm && (
              <ExistingPaymentMethodSection
                invoice={invoice}
                sessionData={sessionData}
                billGroup={billGroup}
                paymentMethods={activePaymentMethods}
                paymentConfiguration={paymentConfiguration}
                payInvoice={payInvoice}
                isPaying={isPaying}
                setFooterState={setFooterState}
                onClose={onClose}
              />
            )}
            <MRadio value="new">
              <MText fontSize="sm" ml={2} fontWeight="bold">
                New Payment Method
              </MText>
            </MRadio>
          </MStack>
        </MRadioGroup>
      )}

      {isUsingNewPm && (
        <NewPaymentMethodSection
          invoice={invoice}
          sessionData={sessionData}
          billGroup={billGroup}
          paymentConfiguration={paymentConfiguration}
          paymentGatewayId={paymentGatewayId}
          payInvoice={payInvoice}
          createPaymentMethod={createPaymentMethod}
          isPaying={isPaying}
          isCreatingPm={isCreatingPm}
          setFooterState={setFooterState}
          onClose={onClose}
        />
      )}

      {wireTransferMethod && (
        <WireTransferInstructions method={wireTransferMethod} />
      )}
    </PayInvoiceModalShell>
  );
};

/**
 * Existing-PM payment path. Does not depend on Stripe at all — calls the
 * payment endpoint directly with the selected PM id, so it renders and works
 * even if the Stripe token endpoint is slow or failed.
 */
const ExistingPaymentMethodSection = ({
  invoice,
  sessionData,
  billGroup,
  paymentMethods,
  paymentConfiguration,
  payInvoice,
  isPaying,
  setFooterState,
  onClose,
}: {
  invoice: CustomerPortalInvoice;
  sessionData: CustomerPortalSessionData;
  billGroup: CustomerPortalBillGroup;
  paymentMethods: CustomerPortalPaymentMethod[];
  paymentConfiguration?: PaymentGatewayConfigurations;
  payInvoice: ReturnType<typeof useCustomerPortalPayInvoice>['mutateAsync'];
  isPaying: boolean;
  setFooterState: (state: SectionFooterState) => void;
  onClose: () => void;
}) => {
  const defaultPaymentMethodId = useMemo(() => {
    if (
      billGroup.paymentMethodId &&
      paymentMethods.some(({ id }) => id === billGroup.paymentMethodId)
    ) {
      return billGroup.paymentMethodId;
    }
    return paymentMethods[0]?.id ?? '';
  }, [billGroup.paymentMethodId, paymentMethods]);

  const form = useForm<ExistingPaymentMethodFormValues>({
    resolver: zodResolver(ExistingPaymentMethodSchema),
    mode: 'onSubmit',
    defaultValues: { paymentMethodId: defaultPaymentMethodId },
  });

  const { addToast } = useToast();
  const [payError, setPayError] = useState('');

  const selectedMethodId = form.watch('paymentMethodId');
  const selectedMethod = useMemo(
    () => paymentMethods.find(({ id }) => id === selectedMethodId),
    [paymentMethods, selectedMethodId],
  );

  const amountLimitError = useMemo(() => {
    if (!selectedMethod) {
      return null;
    }
    const limits = getPaymentAmountLimits(
      paymentConfiguration,
      selectedMethod.paymentMethodType,
      invoice.currency,
    );
    return validatePaymentAmountLimits(
      limits,
      invoice.amountDue,
      invoice.currency,
    );
  }, [
    paymentConfiguration,
    selectedMethod,
    invoice.amountDue,
    invoice.currency,
  ]);

  const onSubmit = form.handleSubmit(async ({ paymentMethodId }) => {
    setPayError('');
    if (amountLimitError) {
      return;
    }
    try {
      const response = await payInvoice({
        sessionData,
        payload: {
          invoiceId: invoice.id,
          paymentMethodId,
          amount: invoice.amountDue,
        },
      });
      // A declined charge resolves with HTTP 200, so surface it inline and keep
      // the modal open rather than closing as if the payment succeeded.
      if (isPaymentDeclined(response)) {
        setPayError(PAYMENT_DECLINED_MESSAGE);
        return;
      }
      addToast(PAYMENT_SUCCESS_TOAST);
      onClose();
    } catch (ex) {
      handleApiErrorToast(ex);
    }
  });

  useEffect(() => {
    setFooterState({
      isSubmitting: isPaying,
      isPayDisabled: isPaying || !!amountLimitError,
    });
  }, [setFooterState, isPaying, amountLimitError]);

  return (
    <MBox as="form" id={FORM_ID} onSubmit={onSubmit}>
      <ExistingPaymentMethodFields
        form={form}
        paymentMethods={paymentMethods}
        amountLimitError={amountLimitError}
        payError={payError}
      />
    </MBox>
  );
};

/**
 * New-PM payment path. Lazily fetches the Stripe SetupIntent + public key,
 * then wraps the form in <StripeWrapper> so useStripe()/useElements() are
 * available. Kept isolated so a slow/failed token fetch only blocks this tab
 * — existing-PM users can still pay.
 */
const NewPaymentMethodSection = ({
  invoice,
  sessionData,
  billGroup,
  paymentConfiguration,
  paymentGatewayId,
  payInvoice,
  createPaymentMethod,
  isPaying,
  isCreatingPm,
  setFooterState,
  onClose,
}: {
  invoice: CustomerPortalInvoice;
  sessionData: CustomerPortalSessionData;
  billGroup: CustomerPortalBillGroup;
  paymentConfiguration?: PaymentGatewayConfigurations;
  paymentGatewayId: string;
  payInvoice: ReturnType<typeof useCustomerPortalPayInvoice>['mutateAsync'];
  createPaymentMethod: ReturnType<
    typeof useCreateCustomerPortalPaymentMethod
  >['mutateAsync'];
  isPaying: boolean;
  isCreatingPm: boolean;
  setFooterState: (state: SectionFooterState) => void;
  onClose: () => void;
}) => {
  const [clientSecret, setClientSecret] = useState<string>();
  const [publicKey, setPublicKey] = useState<string>();
  const [tokenError, setTokenError] = useState<string>();

  const { mutateAsync: generateToken, isPending: isGeneratingToken } =
    useGenerateCustomerPortalPaymentMethodToken();

  // Depend on the primitive session fields rather than the `sessionData`
  // object so a re-render from the parent (which always passes a fresh object
  // literal) doesn't refire this effect before `clientSecret` is set and
  // trigger a duplicate token fetch.
  const { accessToken, accountId, billGroupId, sessionId, tenantId, email } =
    sessionData;
  useEffect(() => {
    if (clientSecret) {
      return;
    }
    generateToken({
      sessionData: {
        accessToken,
        accountId,
        billGroupId,
        sessionId,
        tenantId,
        email,
      },
      payload: { paymentGatewayId, invoiceId: invoice.id },
    })
      .then((response) => {
        setClientSecret(response.paymentGateway.clientSecret);
        setPublicKey(response.paymentGateway.publicKey);
      })
      .catch(() => {
        setTokenError(
          'Unable to initialize payment. Please try again or contact support.',
        );
      });
  }, [
    accessToken,
    accountId,
    invoice.id,
    billGroupId,
    clientSecret,
    email,
    generateToken,
    paymentGatewayId,
    sessionId,
    tenantId,
  ]);

  // While the token is loading or has errored, the form can't be submitted —
  // disable Pay. The inner NewPaymentMethodForm takes over once Stripe is
  // ready and updates the footer state with its own submit/disabled logic.
  const isFormUnavailable =
    !!tokenError || isGeneratingToken || !clientSecret || !publicKey;
  useEffect(() => {
    if (isFormUnavailable) {
      setFooterState({ isSubmitting: false, isPayDisabled: true });
    }
  }, [setFooterState, isFormUnavailable]);

  if (tokenError) {
    return (
      <MBox mt={3} color="tRed.base" fontSize="sm">
        {tokenError}
      </MBox>
    );
  }

  if (isGeneratingToken || !clientSecret || !publicKey) {
    return (
      <MFlex mt={4} justify="center">
        <MPageLoader maxH={100} />
      </MFlex>
    );
  }

  return (
    <StripeWrapper apiKey={publicKey} clientSecret={clientSecret}>
      <NewPaymentMethodForm
        invoice={invoice}
        sessionData={sessionData}
        billGroup={billGroup}
        paymentConfiguration={paymentConfiguration}
        paymentGatewayId={paymentGatewayId}
        payInvoice={payInvoice}
        createPaymentMethod={createPaymentMethod}
        isPaying={isPaying}
        isCreatingPm={isCreatingPm}
        setFooterState={setFooterState}
        onClose={onClose}
      />
    </StripeWrapper>
  );
};

const NewPaymentMethodForm = ({
  invoice,
  sessionData,
  billGroup,
  paymentConfiguration,
  paymentGatewayId,
  payInvoice,
  createPaymentMethod,
  isPaying,
  isCreatingPm,
  setFooterState,
  onClose,
}: {
  invoice: CustomerPortalInvoice;
  sessionData: CustomerPortalSessionData;
  billGroup: CustomerPortalBillGroup;
  paymentConfiguration?: PaymentGatewayConfigurations;
  paymentGatewayId: string;
  payInvoice: ReturnType<typeof useCustomerPortalPayInvoice>['mutateAsync'];
  createPaymentMethod: ReturnType<
    typeof useCreateCustomerPortalPaymentMethod
  >['mutateAsync'];
  isPaying: boolean;
  isCreatingPm: boolean;
  setFooterState: (state: SectionFooterState) => void;
  onClose: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { addToast } = useToast();

  const form = useForm<NewPaymentMethodFormValues>({
    resolver: zodResolver(NewPaymentMethodSchema),
    mode: 'onSubmit',
    defaultValues: {
      paymentMethodName: '',
      billingDetails: {
        fullName: billGroup.billingContact?.fullName || '',
        email: billGroup.billingContact?.email || '',
        phone: billGroup.billingContact?.phone || '',
      },
    },
  });

  const [stripeMethodType, setStripeMethodType] = useState<
    string | undefined
  >();
  const [isStripeReady, setIsStripeReady] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isConfirmingStripe, setIsConfirmingStripe] = useState(false);

  const amountLimitError = useMemo(() => {
    if (!stripeMethodType) {
      return null;
    }
    const methodType = stripeTypeToPaymentMethodType(stripeMethodType);
    if (!methodType) {
      return null;
    }
    const limits = getPaymentAmountLimits(
      paymentConfiguration,
      methodType,
      invoice.currency,
    );
    return validatePaymentAmountLimits(
      limits,
      invoice.amountDue,
      invoice.currency,
    );
  }, [
    paymentConfiguration,
    stripeMethodType,
    invoice.amountDue,
    invoice.currency,
  ]);

  const isSubmitting = isPaying || isCreatingPm || isConfirmingStripe;

  const onSubmit = form.handleSubmit(async (values) => {
    setSaveError('');
    if (amountLimitError || !stripe || !elements) {
      return;
    }

    setIsConfirmingStripe(true);
    try {
      const elementValidation = await elements.submit();
      if (elementValidation.error) {
        setSaveError(
          elementValidation.error.message || 'Invalid payment details',
        );
        return;
      }

      const confirmation = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
          payment_method_data: {
            billing_details: {
              name: values.billingDetails.fullName,
              email: values.billingDetails.email || undefined,
              phone: values.billingDetails.phone || undefined,
            },
          },
        },
        redirect: 'if_required',
      });

      if (confirmation.error) {
        setSaveError(
          confirmation.error.message ||
            'Unable to verify payment method. Please try again.',
        );
        return;
      }

      const { setupIntent } = confirmation;
      if (!setupIntent?.id) {
        setSaveError('Unable to verify payment method. Please try again.');
        return;
      }

      // v1 does not handle microdeposit / 3DS-redirect flows. Those would
      // require an atom-backed redirect-recovery callback route, which the
      // customer portal doesn't have today.
      if (
        setupIntent.status !== 'succeeded' &&
        setupIntent.status !== 'processing'
      ) {
        setSaveError(
          'Payment method verification is not complete yet. Please try again.',
        );
        return;
      }

      const created = await createPaymentMethod({
        sessionData,
        payload: {
          paymentGatewayId,
          paymentMethod: {
            paymentToken: setupIntent.id,
            paymentMethodName: values.paymentMethodName,
            amount: 0,
            description: '',
            billingDetails: {
              fullName: values.billingDetails.fullName,
              email: values.billingDetails.email || undefined,
              phone: values.billingDetails.phone || undefined,
            },
          },
        },
      });

      const payment = await payInvoice({
        sessionData,
        payload: {
          invoiceId: invoice.id,
          paymentMethodId: created.id,
          amount: invoice.amountDue,
        },
      });

      // A declined charge resolves with HTTP 200, so surface it inline and keep
      // the modal open rather than closing as if the payment succeeded. The new
      // payment method has already been saved, so a retry can reuse it.
      if (isPaymentDeclined(payment)) {
        setSaveError(PAYMENT_DECLINED_MESSAGE);
        return;
      }

      addToast(PAYMENT_SUCCESS_TOAST);
      onClose();
    } catch (ex) {
      handleApiErrorToast(ex);
    } finally {
      setIsConfirmingStripe(false);
    }
  });

  const isPayDisabled =
    isSubmitting ||
    !!amountLimitError ||
    !isStripeReady ||
    !stripe ||
    !elements;

  useEffect(() => {
    setFooterState({ isSubmitting, isPayDisabled });
  }, [setFooterState, isSubmitting, isPayDisabled]);

  return (
    <MBox as="form" id={FORM_ID} onSubmit={onSubmit}>
      <NewPaymentMethodFields
        form={form}
        onPaymentElementChange={({
          complete,
          value,
        }: StripePaymentElementChangeEvent) => {
          setIsStripeReady(complete);
          setStripeMethodType(value?.type);
        }}
        amountLimitError={amountLimitError}
        saveError={saveError}
      />
    </MBox>
  );
};

const PayInvoiceModalShell = ({
  onClose,
  footer,
  isLoading,
  children,
}: {
  onClose: () => void;
  footer?: React.ReactNode;
  isLoading?: boolean;
  children: React.ReactNode;
}) => (
  <MCenterModal
    isOpen
    onClose={onClose}
    modalTitle="Pay Invoice"
    size="lg"
    closeOnOverlayClick={false}
    closeOnEsc={false}
    isLoading={isLoading}
    renderFooter={footer ? () => footer : null}
  >
    {children}
  </MCenterModal>
);

const AmountDueHeader = ({ invoice }: { invoice: CustomerPortalInvoice }) => (
  <MGrid
    mb={2}
    templateColumns={{
      base: '1fr',
      sm: 'repeat(2, 1fr)',
      md: 'repeat(3, 1fr)',
    }}
    gap={4}
  >
    <MBox>
      <MText fontSize="sm" color="tGray.darkPurple">
        Invoice
      </MText>
      <MText fontSize="md" fontWeight="600" color="tPurple.dark">
        {invoice.invoiceNumber}
      </MText>
    </MBox>
    <MBox>
      <MText fontSize="sm" color="tGray.darkPurple">
        Due Date
      </MText>
      <MText fontSize="md" fontWeight="600" color="tPurple.dark">
        {toDateShort(invoice.dueDate)}
      </MText>
    </MBox>
    <MBox>
      <MText fontSize="sm" color="tGray.darkPurple">
        Amount Due
      </MText>
      <MText fontSize="xl" fontWeight="700" color="tPurple.dark">
        {formatCurrency(invoice.amountDue, {
          currency: invoice.currency,
          maximumFractionDigits: 2,
        })}
      </MText>
    </MBox>
  </MGrid>
);

const ExistingPaymentMethodFields = ({
  form,
  paymentMethods,
  amountLimitError,
  payError,
}: {
  form: UseFormReturn<ExistingPaymentMethodFormValues>;
  paymentMethods: CustomerPortalPaymentMethod[];
  amountLimitError: string | null;
  payError: string;
}) => {
  const paymentMethodItems = useMemo(
    () =>
      getGroupedAndSortedPaymentMethodOptions(
        paymentMethods.map((pm) => ({ ...pm, name: pm.paymentMethodName })),
      ),
    [paymentMethods],
  );
  const {
    control,
    formState: { errors },
  } = form;

  return (
    <MBox>
      <MFormField
        label="Payment Method"
        isRequired
        error={
          errors.paymentMethodId ||
          (amountLimitError
            ? { message: amountLimitError, type: 'manual' }
            : undefined)
        }
      >
        <Controller
          control={control}
          name="paymentMethodId"
          render={({ field }) => (
            <MCustomSelect
              items={paymentMethodItems}
              itemTitle="name"
              itemValue="id"
              renderItemContent={PaymentMethodItemRenderer}
              {...field}
            />
          )}
        />
      </MFormField>
      {!!payError && (
        <MBox mt={3} color="tRed.base" fontSize="sm">
          {payError}
        </MBox>
      )}
    </MBox>
  );
};

const NewPaymentMethodFields = ({
  form,
  onPaymentElementChange,
  amountLimitError,
  saveError,
}: {
  form: UseFormReturn<NewPaymentMethodFormValues>;
  onPaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
  amountLimitError: string | null;
  saveError: string;
}) => {
  const {
    control,
    formState: { errors },
  } = form;

  return (
    <MBox mt={2}>
      <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
        <MGridItem colSpan={12}>
          <PaymentElement
            onChange={onPaymentElementChange}
            options={{
              layout: {
                type: 'accordion',
                radios: true,
                defaultCollapsed: false,
                spacedAccordionItems: true,
              },
              terms: {
                usBankAccount: 'never',
                card: 'never',
              },
              fields: {
                billingDetails: {
                  name: 'never',
                  email: 'never',
                  phone: 'auto',
                  address: 'auto',
                },
              },
            }}
          />
        </MGridItem>
        <MGridItem colSpan={{ base: 12, md: 6 }}>
          <MFormField
            label="Payment Method Name"
            isRequired
            error={errors.paymentMethodName}
          >
            <Controller
              control={control}
              name="paymentMethodName"
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={{ base: 12, md: 6 }}>
          <MFormField
            label="Full Name"
            isRequired
            error={errors.billingDetails?.fullName}
          >
            <Controller
              control={control}
              name="billingDetails.fullName"
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={{ base: 12, md: 6 }}>
          <MFormField label="Email" error={errors.billingDetails?.email}>
            <Controller
              control={control}
              name="billingDetails.email"
              render={({ field }) => <MInput {...field} type="email" />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={{ base: 12, md: 6 }}>
          <MFormField label="Phone">
            <Controller
              control={control}
              name="billingDetails.phone"
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
      </MGrid>
      {!!amountLimitError && (
        <MBox mt={3} color="tRed.base" fontSize="sm">
          {amountLimitError}
        </MBox>
      )}
      {!!saveError && (
        <MBox mt={3} color="tRed.base" fontSize="sm">
          {saveError}
        </MBox>
      )}
    </MBox>
  );
};

const WireTransferInstructions = ({
  method,
}: {
  method: CustomerPortalPaymentMethod;
}) => {
  const rows: { label: string; value: string }[] = [];
  if (method.bankName) {
    rows.push({ label: 'Bank', value: method.bankName });
  }
  if (method.accountNumber) {
    rows.push({ label: 'Account Number', value: method.accountNumber });
  }
  if (method.routingNumber) {
    rows.push({ label: 'Routing Number', value: method.routingNumber });
  }
  if (method.swiftCode) {
    rows.push({ label: 'SWIFT', value: method.swiftCode });
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <MBox
      mt={5}
      p={4}
      borderRadius="md"
      bg="tBlue.hover"
      border="1px solid"
      borderColor="tBlue.lightShade"
    >
      <MText fontSize="sm" fontWeight="bold" color="tPurple.dark" mb={1}>
        Or pay by wire transfer
      </MText>
      <MText fontSize="sm" color="tGray.darkPurple" mb={3}>
        Send payment to the bank details below.
      </MText>
      <MStack gap={1}>
        {rows.map(({ label, value }) => (
          <MFlex key={label} justifyContent="space-between" gap={4}>
            <MText fontSize="sm" color="tGray.darkPurple">
              {label}
            </MText>
            <MText fontSize="sm" fontWeight="600" color="tPurple.dark">
              {value}
            </MText>
          </MFlex>
        ))}
      </MStack>
    </MBox>
  );
};
